import { z } from "zod";

/**
 * Shared DTO cho **Admin Collection Schedules** (US-AE16.F2-0370-S02 · @UC9635 @UC9636).
 *
 * 🔴🔴 VÌ SAO CÓ SURFACE NÀY — bug #6 rộng hơn bản đồ ghi.
 *
 * `recurring-collection.job.ts` chỉ lọc `scheduleStatus='active'`; nó **không** join
 * `group.archivedAt` lẫn `group.status`. Hệ quả: nhóm đã **đóng sổ** hoặc bị **tạm khoá** vẫn bị máy
 * đẻ `member_dues` mỗi kỳ. API chặn write khi archived (409) nhưng **worker là đường ghi THỨ HAI
 * không đi qua `FundAccessGuard`** — đúng mẫu hình *"guard canh nhầm chỗ"*.
 *
 * ⇒ Trước task này, **hệ thống không có bất kỳ công cụ nào, ở bất kỳ vai nào**, dừng được nguồn sinh
 * nghĩa vụ tiền khi sự cố đang xảy ra.
 *
 * Task này làm **mặt admin + VAN TAY**. Vế **sửa gốc worker** (join `archivedAt`/`status` vào điều
 * kiện `findMany`) thuộc `US-E24.F1-0350` lane app — cố ý KHÔNG trộn hai rủi ro tiền vào một PR, vì
 * gộp là mất khả năng `git bisect` khi có sự cố tiền.
 *
 * Van tay hoạt động ngay cả khi gốc chưa vá, vì `scheduleStatus` **đã** là điều kiện worker đang lọc.
 *
 * INVARIANTS:
 *  - B1: `amountPerMemberMinor` = BigInt minor → **string**, redact theo vai (`null` cho vai không
 *    xem tiền). KHÔNG `0` — `"0"` (thu 0 đồng) và `null` (không được xem) là HAI sự thật khác nhau.
 *  - `.strict()` — field lạ ⇒ 400 tại biên.
 *  - Cursor keyset bắt buộc (`createdAt DESC, id DESC`).
 *  - `pause` = đổi `scheduleStatus`, **KHÔNG** DELETE, **KHÔNG** đụng `member_dues` đã sinh
 *    (§3.6 P4.3 *"máy giữ sổ, người giữ tình"* — máy không tự xoá nghĩa vụ đã phát sinh).
 */

/** Max page-size cho list lịch thu. */
export const ADMIN_COLLECTION_SCHEDULES_MAX_LIMIT = 100;

/**
 * Whitelist `scheduleStatus` — mirror CHECK db `collection_schedules_status_check`
 * (= `SCHEDULE_STATUSES` của domain). Khai ở ĐÂY vì contract admin cần enum để validate query;
 * giá trị phải khớp CHECK, có int-spec tự-khám-phá canh (bài học drift 2 whitelist txType).
 */
export const COLLECTION_SCHEDULE_STATUSES = ["active", "paused", "cancelled"] as const;

export const collectionScheduleStatusSchema = z.enum(COLLECTION_SCHEDULE_STATUSES);

export type CollectionScheduleStatus = z.infer<typeof collectionScheduleStatusSchema>;

/** Query list — lọc theo nhóm / quỹ / trạng thái lịch. Vắng hết ⇒ liệt kê toàn bộ (cursor). */
export const adminCollectionSchedulesQuerySchema = z
  .object({
    groupId: z.string().uuid().optional(),
    fundId: z.string().uuid().optional(),
    status: collectionScheduleStatusSchema.optional(),
    cursor: z.string().optional(),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(ADMIN_COLLECTION_SCHEDULES_MAX_LIMIT)
      .optional(),
  })
  .strict();

export type AdminCollectionSchedulesQuery = z.infer<typeof adminCollectionSchedulesQuerySchema>;

/**
 * List item. Tiền là `string | null` (redact theo vai). `groupArchivedAt`/`groupStatus` đi kèm CỐ Ý:
 * chính chúng cho OPS biết lịch nào đang chạy trên nhóm lẽ ra đã dừng — đó là toàn bộ mục đích của
 * màn này, và là thứ không có ở bất kỳ đâu khác trong portal.
 */
export const adminCollectionScheduleItemSchema = z
  .object({
    id: z.string(),
    fundId: z.string(),
    fundName: z.string().nullable(),
    groupId: z.string(),
    groupName: z.string().nullable(),
    name: z.string(),
    frequency: z.string(),
    scheduleStatus: collectionScheduleStatusSchema,
    startDate: z.string(),
    dueDate: z.string().nullable(),
    /** BigInt minor → string; `null` = **không được xem** (KHÔNG phải "0 đồng"). */
    amountPerMemberMinor: z.string().nullable(),
    currency: z.string(),
    createdAt: z.string(),
    /** Vòng đời NHÓM chủ quản — không redact (không phải tiền). */
    groupStatus: z.enum(["active", "suspended"]),
    groupArchivedAt: z.string().nullable(),
  })
  .strict();

export type AdminCollectionScheduleItem = z.infer<typeof adminCollectionScheduleItemSchema>;

export const adminCollectionSchedulesResponseSchema = z.object({
  data: z.array(adminCollectionScheduleItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type AdminCollectionSchedulesResponse = z.infer<
  typeof adminCollectionSchedulesResponseSchema
>;

/** Độ dài tối đa lý do tạm dừng. */
export const PAUSE_SCHEDULE_REASON_MAX_LENGTH = 500;

/**
 * Body `POST /v1/admin/collection-schedules/{id}/pause`.
 *
 * `reason` **BẮT BUỘC**: đây là hành động dừng một dòng tiền định kỳ của một nhóm người thật. Sáu
 * tháng sau, câu hỏi *"ai dừng và vì sao"* phải trả lời được từ `audit_log`, không phải từ trí nhớ.
 */
export const pauseCollectionScheduleBodySchema = z
  .object({
    reason: z.string().trim().min(1).max(PAUSE_SCHEDULE_REASON_MAX_LENGTH),
  })
  .strict();

export type PauseCollectionScheduleBody = z.infer<typeof pauseCollectionScheduleBodySchema>;

/**
 * Response pause. `alreadyPaused=true` ⇒ no-op (gọi lần 2) — trả 200 chứ không 409: dừng một thứ đã
 * dừng là **đúng ý người bấm**, không phải xung đột.
 */
export const pauseCollectionScheduleResponseSchema = z
  .object({
    scheduleId: z.string(),
    scheduleStatus: collectionScheduleStatusSchema,
    alreadyPaused: z.boolean(),
  })
  .strict();

export type PauseCollectionScheduleResponse = z.infer<
  typeof pauseCollectionScheduleResponseSchema
>;
